import React, { useState, useCallback } from 'react';
import { Table, Button, Input, InputNumber, Select, Space, Popconfirm, Modal, message } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FN110, FN114, FN115, n202Api } from '../../../services/n202Api';

interface Tab5Props {
  formData: Partial<FN110>;
  rows: Partial<FN114>[];
  isEditing: boolean;
  onRowsChange: (updater: (prev: Partial<FN114>[]) => Partial<FN114>[]) => void;
  onMasterFieldChange?: (key: keyof FN110, value: FN110[keyof FN110]) => void;
  isFieldDisabled?: (key: string) => boolean;
}

const Tab5ReceiptDetails: React.FC<Tab5Props> = ({
  formData, rows, isEditing, onRowsChange, onMasterFieldChange, isFieldDisabled,
}) => {
  const [colorModalIdx, setColorModalIdx] = useState<number | null>(null);
  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const [sourceDocumentNo, setSourceDocumentNo] = useState('');
  const [sourceLineNo, setSourceLineNo] = useState(1);

  const handleCellChange = useCallback((idx: number, key: keyof FN114, value: FN114[keyof FN114]) => {
    onRowsChange(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  }, [onRowsChange]);

  const handleAdd = useCallback(() => {
    setSourceModalOpen(true);
  }, []);

  const handleImport = useCallback(async () => {
    try {
      const imported = await n202Api.previewReceiptDetail(sourceDocumentNo.trim(), sourceLineNo);
      const maxSeq = rows.reduce((max, row) => Math.max(max, row.fn114_02 || 0), 0);
      imported.fn114_01 = formData.fn110_01 || '';
      imported.fn114_02 = maxSeq + 1;
      imported.colors = (imported.colors || []).map(color => ({ ...color, fn115_01: imported.fn114_01, fn115_02: imported.fn114_02 }));
      onRowsChange(prev => [...prev, imported]);
      setSourceModalOpen(false);
      setSourceDocumentNo('');
      setSourceLineNo(1);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '無法從來源繳庫單帶入明細');
    }
  }, [sourceDocumentNo, sourceLineNo, rows, formData.fn110_01, onRowsChange]);

  const handleDelete = useCallback((idx: number) => {
    onRowsChange(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn114_02: i + 1 })));
  }, [onRowsChange]);

  // Color sub-detail handlers
  const handleColorAdd = useCallback((receiptIdx: number) => {
    onRowsChange(prev => prev.map((r, i) => {
      if (i !== receiptIdx) return r;
      const colors = r.colors || [];
      const maxSeq = colors.reduce((max, c) => Math.max(max, c.fn115_03 || 0), 0);
      const newColor: FN115 = {
        fn115_01: formData.fn110_01 || '',
        fn115_02: r.fn114_02 || 0,
        fn115_03: maxSeq + 1,
        fn115_11: '', fn115_12: '', fn115_13: '', fn115_14: 0,
        fn115_90: 0, fn115_91: '', fn115_92: null, fn115_93: '',
      };
      return { ...r, colors: [...colors, newColor] };
    }));
  }, [formData.fn110_01, onRowsChange]);

  const handleColorChange = useCallback((receiptIdx: number, colorIdx: number, key: keyof FN115, value: FN115[keyof FN115]) => {
    onRowsChange(prev => prev.map((r, i) => {
      if (i !== receiptIdx) return r;
      const colors = (r.colors || []).map((c, ci) => ci === colorIdx ? { ...c, [key]: value } : c);
      return { ...r, colors };
    }));
  }, [onRowsChange]);

  const handleColorDelete = useCallback((receiptIdx: number, colorIdx: number) => {
    onRowsChange(prev => prev.map((r, i) => {
      if (i !== receiptIdx) return r;
      const colors = (r.colors || []).filter((_, ci) => ci !== colorIdx).map((c, ci) => ({ ...c, fn115_03: ci + 1 }));
      return { ...r, colors };
    }));
  }, [onRowsChange]);

  const columns: ColumnsType<Partial<FN114>> = [
    { title: <span data-contract-field="fn114_02">#</span>, dataIndex: 'fn114_02', width: 50, render: (_v, _r, i) => i + 1 },
    {
      title: <span data-contract-field="fn114_11">入庫單號</span>, dataIndex: 'fn114_11', width: 140,
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn114_11', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn114_12">項次</span>, dataIndex: 'fn114_12', width: 70, align: 'center' as const,
      render: (v, _r, i) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => handleCellChange(i, 'fn114_12', val || 0)} style={{ width: '100%' }} />
        : v,
    },
    {
      title: <span data-contract-field="fn114_13">類型</span>, dataIndex: 'fn114_13', width: 80,
      render: (v, _r, i) => isEditing
        ? <Select size="small" value={v || 1} onChange={val => handleCellChange(i, 'fn114_13', val)} style={{ width: '100%' }} options={[{ value: 1, label: '繳庫' }, { value: 2, label: '託工繳庫' }]} />
        : v,
    },
    {
      title: <span data-contract-field="fn114_21">入庫日期</span>, dataIndex: 'fn114_21', width: 110,
      render: (v, _r, i) => isEditing
        ? <Input size="small" type="date" value={v || ''} onChange={e => handleCellChange(i, 'fn114_21', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn114_22">色號數量合計</span>, dataIndex: 'fn114_22', width: 120, align: 'right' as const,
      render: (v, _r, i) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => handleCellChange(i, 'fn114_22', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(),
    },
    {
      title: <span data-contract-field="fn114_14">產品代號</span>, dataIndex: 'fn114_14', width: 120,
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn114_14', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn114_15">繳庫量</span>, dataIndex: 'fn114_15', width: 100, align: 'right' as const,
      render: (v, _r, i) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => handleCellChange(i, 'fn114_15', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(),
    },
    {
      title: <span data-contract-field="fn114_16">金額</span>, dataIndex: 'fn114_16', width: 120, align: 'right' as const,
      render: (v, _r, i) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => handleCellChange(i, 'fn114_16', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(),
    },
    {
      title: <span data-contract-field="fn114_51">廠商代碼</span>, dataIndex: 'fn114_51', width: 120,
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn114_51', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn115_11">色號</span>, width: 70, align: 'center' as const,
      render: (_v, r, i) => (
        <Button
          size="small"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => setColorModalIdx(i)}
        >
          {(r.colors || []).length}
        </Button>
      ),
    },
    ...(isEditing ? [{
      title: '操作', width: 60, align: 'center' as const,
      render: (_v: unknown, _r: Partial<FN114>, i: number) => (
        <Popconfirm title="確認刪除?" onConfirm={() => handleDelete(i)}>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  // Color sub-detail columns
  const colorColumns: ColumnsType<Partial<FN115>> = [
    { title: <span data-contract-field="fn115_03">#</span>, width: 50, render: (_v, _r, i) => i + 1 },
    {
      title: <span data-contract-field="fn115_11">色號代碼</span>, dataIndex: 'fn115_11',
      render: (v, _r, i) => isEditing && colorModalIdx !== null
        ? <Input size="small" value={v} onChange={e => handleColorChange(colorModalIdx, i, 'fn115_11', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn115_12">色號名稱</span>, dataIndex: 'fn115_12',
      render: (v, _r, i) => isEditing && colorModalIdx !== null
        ? <Input size="small" value={v} onChange={e => handleColorChange(colorModalIdx, i, 'fn115_12', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn115_14">數量</span>, dataIndex: 'fn115_14', width: 120, align: 'right' as const,
      render: (v, _r, i) => isEditing && colorModalIdx !== null
        ? <InputNumber size="small" value={v} onChange={val => handleColorChange(colorModalIdx, i, 'fn115_14', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(),
    },
    ...(isEditing && colorModalIdx !== null ? [{
      title: '操作', width: 60, align: 'center' as const,
      render: (_v: unknown, _r: Partial<FN115>, i: number) => (
        <Popconfirm title="確認刪除?" onConfirm={() => handleColorDelete(colorModalIdx!, i)}>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <div>
      {/* Header fields */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto 1fr)', gap: '8px 12px', marginBottom: 12, alignItems: 'center' }}>
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>入庫明細筆數</span>
        <Input size="small" disabled value={String(rows.length || 0)} style={{ maxWidth: 80 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>關聯業務客訴單號1</span>
        <Input size="small" disabled={isFieldDisabled?.('fn110_71') ?? !isEditing} value={formData.fn110_71 || ''} onChange={e => onMasterFieldChange?.('fn110_71', e.target.value)} style={{ maxWidth: 160 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>關聯業務客訴單號2</span>
        <Input size="small" disabled={isFieldDisabled?.('fn110_72') ?? !isEditing} value={formData.fn110_72 || ''} onChange={e => onMasterFieldChange?.('fn110_72', e.target.value)} style={{ maxWidth: 160 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>關聯業務客訴單號3</span>
        <Input size="small" disabled={isFieldDisabled?.('fn110_73') ?? !isEditing} value={formData.fn110_73 || ''} onChange={e => onMasterFieldChange?.('fn110_73', e.target.value)} style={{ maxWidth: 160 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>關聯業務客訴單號4</span>
        <Input size="small" disabled={isFieldDisabled?.('fn110_74') ?? !isEditing} value={formData.fn110_74 || ''} onChange={e => onMasterFieldChange?.('fn110_74', e.target.value)} style={{ maxWidth: 160 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>關聯業務客訴單號5</span>
        <Input size="small" disabled={isFieldDisabled?.('fn110_75') ?? !isEditing} value={formData.fn110_75 || ''} onChange={e => onMasterFieldChange?.('fn110_75', e.target.value)} style={{ maxWidth: 160 }} />
      </div>

      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增入庫明細</Button>
        </Space>
      )}

      <Table<Partial<FN114>>
        columns={columns}
        dataSource={rows}
        rowKey={(_r, i) => String(i)}
        size="small"
        pagination={false}
        bordered
        scroll={{ y: 340 }}
      />

      <Modal title="從繳庫單帶入" open={sourceModalOpen} onOk={() => void handleImport()} onCancel={() => setSourceModalOpen(false)} okButtonProps={{ disabled: !sourceDocumentNo.trim() || sourceLineNo <= 0 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input value={sourceDocumentNo} onChange={e => setSourceDocumentNo(e.target.value)} placeholder="繳庫單號" />
          <InputNumber min={1} value={sourceLineNo} onChange={value => setSourceLineNo(value || 1)} placeholder="項次" style={{ width: '100%' }} />
        </Space>
      </Modal>

      {/* Color sub-detail modal */}
      <Modal
        title={`色號明細 (入庫項 #${colorModalIdx !== null ? colorModalIdx + 1 : ''})`}
        open={colorModalIdx !== null}
        onCancel={() => setColorModalIdx(null)}
        footer={null}
        width={600}
      >
        {colorModalIdx !== null && (
          <>
            {isEditing && (
              <Space style={{ marginBottom: 8 }}>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => handleColorAdd(colorModalIdx)}>新增色號</Button>
              </Space>
            )}
            <Table<Partial<FN115>>
              columns={colorColumns}
              dataSource={rows[colorModalIdx]?.colors || []}
              rowKey={(_r, i) => String(i)}
              size="small"
              pagination={false}
              bordered
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default React.memo(Tab5ReceiptDetails);
